import React, { useState, useEffect } from 'react';
import { Calendar, ExternalLink, Newspaper, Search, Filter, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// Strip HTML tags from excerpt
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

// Placeholder image
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
  <rect fill="#f3f4f6" width="400" height="300"/>
  <text fill="#9ca3af" font-family="Arial,sans-serif" font-size="48" text-anchor="middle" x="200" y="160">📰</text>
</svg>
`);

// Categories for filtering
const CATEGORIES = [
  { value: 'all', label: 'All News' },
  { value: 'mep', label: 'MEP' },
  { value: 'construction', label: 'Construction' },
  { value: 'sustainability', label: 'Sustainability' },
  { value: 'regulations', label: 'Regulations' },
  { value: 'bim', label: 'BIM' }
];

// News Card Component
const NewsCard = ({ article, featured = false }) => {
  return (
    <Card 
      className={`group h-full flex ${featured ? 'md:flex-row' : 'flex-col'} overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-200 bg-white`}
      data-testid={`news-card-${article.id}`}
    >
      {/* Thumbnail */}
      <div className={`${featured ? 'md:w-2/5' : ''} aspect-video bg-gray-100 overflow-hidden relative`}>
        <img
          src={article.thumbnail || PLACEHOLDER_IMAGE}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
        />
        {article.pinned && (
          <span className="absolute top-3 right-3 px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded">
            FEATURED
          </span>
        )}
        <span className="absolute top-3 left-3 px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded capitalize">
          {article.category || 'News'}
        </span>
      </div>
      
      <CardContent className={`p-5 flex-1 flex flex-col ${featured ? 'md:w-3/5' : ''}`}>
        {/* Source and date */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
          <span className="font-medium text-blue-600">{article.sourceName}</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(article.publishDate)}
          </span>
        </div>
        
        {/* Title */}
        <h3 className={`font-semibold text-gray-900 ${featured ? 'text-xl md:text-2xl' : 'text-base'} mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors`}>
          {article.titleOverride || article.title}
        </h3>
        
        {/* Excerpt */}
        <p className={`text-gray-600 ${featured ? 'line-clamp-4' : 'line-clamp-2 text-sm'} mb-4 flex-1`}>
          {stripHtml(article.excerptOverride || article.excerpt)}
        </p>
        
        {/* Read more link */}
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 group/link"
        >
          Read full article
          <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-0.5" />
        </a>
      </CardContent>
    </Card>
  );
};

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const ITEMS_PER_PAGE = 9;

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const categoryParam = category !== 'all' ? `&category=${category}` : '';
        const response = await fetch(`${API}/api/news?limit=${ITEMS_PER_PAGE}&page=${page}${categoryParam}`);
        const data = await response.json();
        
        if (data.success) {
          setArticles(data.articles || []);
          setTotalPages(data.pages || 1);
          setTotal(data.total || 0);
        } else {
          setError('Failed to load news');
        }
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Unable to load news at this time');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [category, page]);

  // Reset page when category changes
  useEffect(() => {
    setPage(1);
  }, [category]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>
          
          <div className="flex items-center gap-3 mb-4">
            <Newspaper className="w-8 h-8" />
            <span className="text-sm font-medium uppercase tracking-wider text-blue-200">
              Stay Informed
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Industry News & Insights
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl">
            The latest updates from building services engineering, MEP, sustainability, net zero, and UK construction industry.
          </p>
        </div>
      </header>

      {/* Filters */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500 hidden md:block" />
            <span className="text-sm text-gray-500 hidden md:block mr-2">Filter:</span>
            
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setCategory(cat.value)}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                  category === cat.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-6 py-12">
        {/* Results info */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-600">
            {loading ? 'Loading...' : `Showing ${articles.length} of ${total} articles`}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-video bg-gray-200" />
                <CardContent className="p-4">
                  <div className="h-3 bg-gray-200 rounded w-1/3 mb-3" />
                  <div className="h-5 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-5 bg-gray-200 rounded w-2/3 mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-16 text-gray-500">
            <Newspaper className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-xl mb-2">{error}</p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && articles.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <Newspaper className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-xl mb-2">No news articles found</p>
            <p className="text-gray-400">Try selecting a different category</p>
          </div>
        )}

        {/* News Grid */}
        {!loading && !error && articles.length > 0 && (
          <>
            {/* Featured article (first pinned or first article) */}
            {page === 1 && articles[0]?.pinned && (
              <div className="mb-8">
                <NewsCard article={articles[0]} featured />
              </div>
            )}
            
            {/* Grid of articles */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles
                .slice(page === 1 && articles[0]?.pinned ? 1 : 0)
                .map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      page === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </main>

      {/* Footer Disclaimer */}
      <footer className="bg-gray-100 py-6 text-center">
        <p className="text-sm text-gray-500">
          External industry news and updates. Articles link to their original sources.
        </p>
      </footer>
    </div>
  );
};

export default NewsPage;
